import SlidePanel from 'components/SlideOver/SlidePanel';
import { groupPath } from 'helper/recordPaths';
import { isDeletedGroupLink, markDeletedGroup } from 'components/record/deletedGroup';
import { Pencil, TrashIcon } from 'assets/icons';
import CardIconButton from 'components/card/CardIconButton';
import AssignMenu from 'components/assign/AssignMenu';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import CommentComposer from 'components/comments/CommentComposer';
import CommentThread from 'components/comments/CommentThread';
import useCommentThread from 'components/comments/useCommentThread';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UndoToast from 'components/toasts/UndoToast';
import Tooltips from 'components/tooltips/Tooltips';
import { apiErrorText } from 'helper/apiErrorText';
import { isRealId, recordOfLink } from 'helper/taskLinks';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTaskPanels from 'hooks/useTaskPanels';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import TaskActivityTab from './TaskActivityTab';
import TaskDetailTab from './TaskDetailTab';
import { TaskActivitySkeleton, TaskCommentsSkeleton, TaskDetailSkeleton } from './TaskPanelSkeletons';

/**
 * The panel of a task (slide-over `get_task`, its id in `objID`): its detail,
 * its activity and its comments, the colleague it is assigned to, and what
 * edits, deletes, ticks or carries it out (call, send the e-mail). Every
 * change tells the lists (useTasksChanged).
 */
const TaskPanel = () => {
  const { objID, setObjID } = UseContext();
  // The task's id, or { taskId, tab } to open it on another tab (useTaskPanels).
  const taskId = typeof objID === 'string' ? objID : objID?.taskId ?? null;
  const initialTab = (typeof objID === 'object' && objID?.tab) || 'detail';
  const { text } = useText('taskPanel');
  const { text: tasksText, language } = useText('tasks');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTaskForm } = useTaskPanels();
  const navigate = useNavigate();
  const notify = useNotifyTasksChanged();
  const [task, setTask] = useState(null);
  const [failed, setFailed] = useState(false);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState('detail');

  const load = useCallback(
    () => call(crmApi.getTask, { id: taskId }).then(setTask, () => setFailed(true)),
    [call, taskId],
  );
  useEffect(() => {
    if (!taskId || !me) return;
    setTask(null);
    setFailed(false);
    setTab(initialTab);
    load();
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
    call(crmApi.getContacts).then(setContacts, () => setContacts([]));
  }, [call, load, taskId, initialTab, me]);

  // Opened, the task is read: its comments no longer count as new.
  useEffect(() => {
    if (task?.unreadComments) call(crmApi.markTaskRead, { id: task.id }).then(notify, () => {});
  }, [call, notify, task?.id, task?.unreadComments]);

  const thread = useCommentThread({
    key: taskId,
    load: () => call(crmApi.listComments, { taskId }),
    add: (body, mentions) => call(crmApi.addComment, { taskId, body, mentions }),
    update: (commentId, body, mentions) => call(crmApi.updateComment, { taskId, commentId, body, mentions }),
    remove: (commentId) => call(crmApi.deleteComment, { taskId, commentId }),
    onCommented: notify,
  });

  const close = () => toggleSlideOver();
  const fail = (error) => {
    const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
    if (message) toast.error(message);
  };
  const attempt = async (run, message, { closes = false } = {}) => {
    try {
      const result = await run();
      if (message) toast.success(message);
      notify();
      if (closes) close();
      else await load();
      return result ?? true;
    } catch (error) {
      fail(error);
      return false;
    }
  };

  const save = (fields) =>
    attempt(
      () => call(crmApi.updateTask, { id: task.id, fields }),
      text('note' in fields ? 'descriptionSaved' : 'emailSaved'),
    );
  const assign = (assigneeId) => {
    const person = team.find((member) => member.id === assigneeId);
    return attempt(
      () => call(crmApi.assignTask, { id: task.id, assigneeId }),
      text('assigned', { name: `${person?.firstname ?? ''} ${person?.lastname ?? ''}`.trim() }),
    );
  };
  const toggleDone = () =>
    attempt(() => call(crmApi.setTaskDone, { id: task.id, done: !task.doneAt }), text(task.doneAt ? 'reopened' : 'done'), {
      closes: true,
    });
  const remove = async () => {
    const deleted = await attempt(() => call(crmApi.deleteTask, { id: task.id }), null, { closes: true });
    if (!deleted) return;
    // Ten seconds to change one's mind: no screen restores a task afterwards.
    toast.success(
      <UndoToast
        message={text('deleted')}
        undoLabel={text('undo')}
        onUndo={() =>
          call(crmApi.restoreTask, { task: deleted }).then(() => {
            toast.success('Tâche restaurée');
            notify();
          }, fail)
        }
      />,
      { autoClose: 10000 },
    );
  };
  // A company (a contact's too) opens its panel, a group its page, a temp
  // worker his panel; the demo's temp workers are not in Jobbiz, so they do
  // not open.
  const openerOf = (link) => {
    if (link.type === 'tempWorker') {
      return isRealId(link.id)
        ? () => {
            setObjID(link.id);
            toggleSlideOver('get_temp_worker');
          }
        : null;
    }
    // A deleted group has no page left to open.
    const target = isDeletedGroupLink(markDeletedGroup(task)(link)) ? null : recordOfLink(link);
    if (!target) return null;
    if (target.kind === 'group') {
      return () => {
        close();
        navigate(groupPath(target.id));
      };
    }
    return () => {
      setObjID(target.id);
      toggleSlideOver('get_company');
    };
  };

  if (failed) {
    return (
      <SlidePanel onClose={close} closeLabel={text('close')}>
        <p className="py-16 text-center text-sm text-gray-600">{text('loadError')}</p>
      </SlidePanel>
    );
  }
  const tabs = [
    { id: 'detail', label: text('tabDetail') },
    { id: 'activity', label: text('tabActivity') },
    { id: 'comments', label: text('tabComments') },
  ];
  // The title and the tabs' content are drawn in grey until the task lands;
  // the tabs themselves are already there.
  if (!task) {
    return (
      <SlidePanel
        title={<span className="block h-7 w-2/3 animate-pulse rounded bg-gray-200" />}
        onClose={close}
        closeLabel={text('close')}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
        bodyClassName="flex flex-col"
      >
        {tab === 'detail' && <TaskDetailSkeleton />}
        {tab === 'activity' && <TaskActivitySkeleton />}
        {tab === 'comments' && <TaskCommentsSkeleton />}
      </SlidePanel>
    );
  }

  // `access` comes from the api and is the only source of rights: a task
  // without it allows no gesture.
  const canEdit = task.access?.canEdit === true;
  const canDelete = task.access?.canDelete === true;
  const assignee = team.find((member) => member.id === task.assigneeId);

  return (
    <SlidePanel
      title={task.title}
      onClose={close}
      closeLabel={text('close')}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      // The assignee's avatar sits right of the tabs, as on the prototype.
      tabsAside={
        <span className="absolute -top-0.5 right-4 md:right-6">
          {canEdit ? (
            <AssignMenu value={task.assigneeId} employees={team} meId={me?.id} onChange={assign} />
          ) : (
            assignee && (
              <Tooltips text={`${assignee.firstname} ${assignee.lastname}`} placement="top">
                <span className="flex">
                  <EmployeeAvatar employee={assignee} />
                </span>
              </Tooltips>
            )
          )}
        </span>
      }
      bodyClassName="flex flex-col"
      belowBody={tab === 'comments' && <CommentComposer people={team} meId={me?.id} onSend={thread.send} />}
      footer={
        <>
          <div className="mr-auto flex items-center gap-2">
            {canEdit && (
              <CardIconButton label={text('edit')} icon={<Pencil wh={18} color="#374151" />} onClick={() => openTaskForm({ taskId: task.id })} />
            )}
            {canDelete && <CardIconButton label={text('delete')} icon={<TrashIcon wh={18} color="#374151" />} onClick={remove} />}
          </div>
          {canEdit && (
            <Button color={task.doneAt ? 'SECONDARY' : 'PRIMARY'} size="SM" onClick={toggleDone}>
              {text(task.doneAt ? 'reopen' : 'markDone')}
            </Button>
          )}
        </>
      }
    >
      {tab === 'detail' && <TaskDetailTab task={task} openerOf={openerOf} onSave={save} canEdit={canEdit} />}
      {tab === 'activity' && <TaskActivityTab task={task} team={team} contacts={contacts} />}
      {tab === 'comments' &&
        (thread.comments ? (
          <CommentThread
            comments={thread.comments}
            people={team}
            meId={me?.id}
            onEdit={thread.edit}
            onDelete={thread.removeComment}
            pendingIds={thread.pendingIds}
          />
        ) : (
          <TaskCommentsSkeleton />
        ))}
    </SlidePanel>
  );
};

export default TaskPanel;
